import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CDataTable,
  CPagination,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import AccountingAccount from "../../models/accounting-accounts";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import AccountingAccountUpdateModal from "./AccountingAccountUpdateModal";
import {
  ACCOUNTING_ENTRIES_CREATE,
  ACCOUNTING_ENTRIES_UPDATE,
} from "../../auth/permissions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";

const ITEMS_PER_PAGE = 50;

const AccountingAccountList = () => {
  const fields = [
    {
      key: "codeIdentifier",
      _classes: ["text-center"],
      label: "Código",
    },
    {
      key: "name",
      _classes: ["text-center"],
      label: "Nombre",
    },
    {
      key: "actions",
      _classes: ["text-center"],
      label: "Acciones",
      filter: false,
    },
  ];

  const [showAccountingAccountEditModal, setShowAccountingAccountEditModal] =
    useState(false);

  const [accountingaccount, setAccountingAccount] = useState<
    AccountingAccount | undefined
  >(undefined);
  const [search, setSearch] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [accountingaccounts, setAccountingAccounts] = useState<
    AccountingAccount[]
  >([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const history = useHistory();

  const getURLParams = () => {
    let urlParams = new URLSearchParams(history.location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchAccountingAccounts = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    const accountingaccountsStatus = await getList<AccountingAccount>(
      "/accounting_accounts/",
      limit,
      offset,
      urlParams,
    );
    if (accountingaccountsStatus.status === SUCCESS) {
      if (accountingaccountsStatus.data !== undefined) {
        const count = accountingaccountsStatus.data.count;
        const pages = Math.ceil((count ? count : 0) / ITEMS_PER_PAGE);
        setTotalPages(pages);
        setAccountingAccounts(accountingaccountsStatus.data.items);
        setCurrentPage(urlParams.get("page"));
      }
    } else {
      const message = accountingaccountsStatus.detail
        ? accountingaccountsStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
    setLoading(false);
  };

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : "",
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const onAddClick = () => {
    setShowAccountingAccountEditModal(true);
  };

  const onUpdateClick = (accountingaccount: AccountingAccount) => {
    setAccountingAccount(accountingaccount);
    setShowAccountingAccountEditModal(true);
  };

  const onAccountingAccountEditCancel = () => {
    setAccountingAccount(undefined);
    setShowAccountingAccountEditModal(false);
  };

  const onAccountingAccountEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowAccountingAccountEditModal(false);
    setAccountingAccounts([]);
    setLoading(true);
    fetchAccountingAccounts();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  useEffect(() => {
    fetchAccountingAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return history.listen((location) => {
      if (location.pathname !== getRoutePath("accountingAccounts")) {
        return;
      }
      fetchAccountingAccounts();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2 ">
                <h3>Cuentas Contables</h3>
              </div>
              <div className="p-2 ">
                <PermissionRequiredComponent
                  permissionName={ACCOUNTING_ENTRIES_CREATE}
                >
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Nuevo
                  </CButton>
                </PermissionRequiredComponent>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol>
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Introduzca el termino de busqueda"
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                    >
                      Buscar
                    </CButton>
                  </CInputGroup>
                </CCol>
              </CRow>
              <div>
                <CDataTable
                  noItemsView={
                    <h2 className="text-center">No se encontraron registros</h2>
                  }
                  addTableClasses={"table-fixed"}
                  fields={fields}
                  items={accountingaccounts}
                  striped
                  border
                  loading={loading}
                  responsive
                  scopedSlots={{
                    actions: (item: AccountingAccount) => {
                      const editButton = (
                        <PermissionRequiredComponent
                          permissionName={ACCOUNTING_ENTRIES_UPDATE}
                        >
                          <CButton
                            className="text-white"
                            color="warning"
                            onClick={() => {
                              onUpdateClick(item);
                            }}
                          >
                            <i className="fa fa-pencil"></i>
                          </CButton>
                        </PermissionRequiredComponent>
                      );

                      return (
                        <td className="text-right">
                          <CButtonGroup>{editButton}</CButtonGroup>
                        </td>
                      );
                    },
                  }}
                />
                <div className="d-flex justify-content-center">
                  <CPagination
                    pages={totalPages}
                    activePage={currentPage}
                    onActivePageChange={(i: number) => onPageChange(i)}
                    className={totalPages < 2 ? "d-none" : ""}
                  />
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <AccountingAccountUpdateModal
        show={showAccountingAccountEditModal}
        onCancel={onAccountingAccountEditCancel}
        onSuccess={onAccountingAccountEditSuccess}
        accountingaccount={accountingaccount}
      />
    </>
  );
};

export default AccountingAccountList;
